import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Building2, KeyRound, LayoutDashboard, LogOut, Mails, Moon, ScrollText, Shield, ShieldCheck, Smartphone, Sun, Workflow } from "lucide-react";
import { AppFooter, useSuiteTheme } from "@athavita/ui-theme";
import { AppLauncher } from "./AppLauncher";

const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/tenants", label: "Tenants", icon: Building2 },
    { to: "/distribution-lists", label: "Distribution lists", icon: Mails },
    { to: "/connect-policies", label: "Connect policies", icon: ShieldCheck },
    { to: "/mobile", label: "Mobile devices", icon: Smartphone },
    { to: "/identity-providers", label: "Identity providers", icon: KeyRound },
    { to: "/service-providers", label: "Service providers", icon: Workflow },
    { to: "/audit", label: "Audit log", icon: ScrollText }
];

export function AdminShell() {
    const auth = useAuth();
    const { theme, toggle } = useSuiteTheme();
    const displayName =
        auth.user?.profile.name ?? auth.user?.profile.email ?? "Signed in";

    const [showShortcuts, setShowShortcuts] = useState(false);

    // Same global-shortcut shape as Mail / Drive / Connect — `?` opens
    // the cheat sheet, `/` focuses the page's filter input. Pauses
    // while focus is in an editable field so typing in a filter doesn't
    // re-trigger focus.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            const tag = target?.tagName ?? "";
            const editing = tag === "INPUT" || tag === "TEXTAREA" ||
                target?.isContentEditable === true;
            if (showShortcuts) return;

            if (e.key === "?") {
                if (editing) return;
                e.preventDefault();
                setShowShortcuts(true);
                return;
            }
            if (editing) return;
            if (e.key === "/") {
                // Filter inputs are per-page; pick the first one rendered
                // (admin pages use one filter at most). Skips when no
                // filter is rendered (Dashboard, Connect policies).
                const input = document.querySelector<HTMLInputElement>('input[type="search"]');
                if (input) {
                    e.preventDefault();
                    input.focus();
                    input.select();
                }
                return;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showShortcuts]);

    return (
        <div className="flex h-screen flex-col bg-background text-foreground">
            <div className="flex min-h-0 flex-1">
            <aside className="flex w-60 shrink-0 flex-col border-r bg-muted/20 p-3">
                <div className="mb-4 flex items-center gap-2 px-2">
                    <AppLauncher currentApp="admin" />
                    <Shield className="h-5 w-5 text-primary" aria-hidden />
                    <span className="font-semibold">Athavita Admin</span>
                </div>
                <nav className="flex flex-col gap-0.5" aria-label="Admin sections">
                    {nav.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            className={({ isActive }) =>
                                `flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                                    isActive
                                        ? "bg-primary/10 text-primary"
                                        : "hover:bg-accent hover:text-accent-foreground"
                                }`
                            }
                        >
                            <item.icon className="h-4 w-4" />
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="mt-auto border-t pt-3">
                    <div className="px-2 pb-2 text-xs text-muted-foreground truncate" title={displayName}>
                        {displayName}
                    </div>
                    <button
                        type="button"
                        onClick={toggle}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm hover:bg-accent"
                        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        {theme === "dark" ? "Light mode" : "Dark mode"}
                    </button>
                    <button
                        type="button"
                        onClick={() => void auth.signoutRedirect()}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm hover:bg-accent"
                    >
                        <LogOut className="h-4 w-4" />
                        Sign out
                    </button>
                </div>
            </aside>
            <main className="min-h-0 flex-1 overflow-auto p-6">
                <Outlet />
            </main>
            {showShortcuts ? (
                <ShortcutsDialog onClose={() => setShowShortcuts(false)} />
            ) : null}
            </div>
            <AppFooter />
        </div>
    );
}

/**
 * Modal cheat-sheet dialog opened by the `?` shortcut. Same fixed-overlay
 * pattern as the Mail / Drive / Connect cheat sheets so the muscle
 * memory transfers across the suite.
 */
function ShortcutsDialog({ onClose }: { onClose: () => void }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const rows: Array<[string, string]> = [
        ["?",   "Show this help"],
        ["/",   "Focus filter on the current page"],
        ["Esc", "Close dialog"],
    ];
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Keyboard shortcuts"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-lg border bg-background p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-base font-semibold">Keyboard shortcuts</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
                        aria-label="Close shortcuts dialog"
                    >
                        Esc
                    </button>
                </div>
                <table className="w-full text-sm">
                    <tbody>
                        {rows.map(([keys, desc]) => (
                            <tr key={desc} className="border-t first:border-0">
                                <td className="w-32 py-1.5 font-mono text-xs text-muted-foreground">{keys}</td>
                                <td className="py-1.5">{desc}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="mt-3 text-[11px] text-muted-foreground">
                    Shortcuts pause while you're typing in a text field.
                </p>
            </div>
        </div>
    );
}
